import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Sofa, Monitor, Heart, BookOpen, Building, Smartphone, Globe, Layers,
  ArrowRight, Sparkles, Zap
} from 'lucide-react'

const services = [
  {
    icon: Sofa,
    title: 'Furniture Solutions',
    description: 'School, classroom, laboratory & office furniture',
    color: '#6366f1',
  },
  {
    icon: Monitor,
    title: 'Digital & Technology',
    description: 'Smart classrooms, software & IT infrastructure',
    color: '#8b5cf6',
  },
  {
    icon: Heart,
    title: 'Health & Nutrition',
    description: 'Nutrition bars, supplements & meal programs',
    color: '#ec4899',
  },
  {
    icon: BookOpen,
    title: 'Education Supplies',
    description: 'Stationery, books & learning materials',
    color: '#f59e0b',
  },
  {
    icon: Building,
    title: 'Infrastructure',
    description: 'Flooring, playground & gym setup',
    color: '#10b981',
  },
  {
    icon: Smartphone,
    title: 'Mobile & Web Development',
    description: 'Custom apps & responsive websites',
    color: '#06b6d4',
  },
  {
    icon: Globe,
    title: 'Website Creation',
    description: 'Web design & e-commerce solutions',
    color: '#f97316',
  },
  {
    icon: Layers,
    title: 'PLM Services',
    description: 'PLM assessment & implementation',
    color: '#84cc16',
  },
  {
    icon: Zap,
    title: 'IoT & Cloud Services',
    description: 'IoT consulting & cloud solutions',
    color: '#14b8a6',
  },
]

export const ServicesGrid = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-20 relative">
      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
            }}
          >
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm text-slate-300">What We Offer</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Services</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Comprehensive enterprise solutions for government and institutional excellence
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => {
            const Icon = service.icon
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <Link
                  to="/services"
                  className="block h-full rounded-2xl p-5 transition-all duration-300 hover:translate-y-[-2px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `${service.color}20`,
                        border: `1px solid ${service.color}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: service.color }} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white group-hover:text-primary transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm text-slate-400">{service.description}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 text-center"
        >
          <Link
            to="/services"
            className="btn-glow inline-flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-xl"
          >
            <Sparkles className="w-4 h-4" />
            <span>View All Services</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}